import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Badge, Button, Card, CardHeader, Col, Row, Table } from "reactstrap";
import {
  faChartBar,
  faCoins,
  faMoneyCheckAlt,
  faPercent,
  faReceipt,
} from "@fortawesome/free-solid-svg-icons";
import HubLinkCard from "../../common/HubLinkCard";
import Money from "./Money";

const formatDate = (date) => {
  const d = new Date(date);
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
};

const cardsFor = ({ base, isManager, data }) => [
  {
    key: "payments",
    to: `${base}/payments/properties`,
    icon: faMoneyCheckAlt,
    title: "Pagos de propietarios",
    description: isManager
      ? "Aprueba o rechaza los pagos que reportan los propietarios."
      : "Pagos reportados por los propietarios del condominio.",
    meta:
      isManager && data.pendingPays.total > 0 ? (
        <Badge color="primary" pill className="text-nowrap">
          {data.pendingPays.total} por aprobar
        </Badge>
      ) : null,
  },
  isManager && {
    key: "receivable",
    to: `${base}/receivable/properties`,
    icon: faReceipt,
    color: "success",
    title: "Efectos por cobrar",
    description: "Lo que debe cada propiedad del condominio.",
    meta: data.receivableTotal.loading ? null : (
      <Money value={data.receivableTotal.value} className="fs--1 font-weight-semi-bold text-nowrap" />
    ),
  },
  {
    key: "aliquots",
    to: `${base}/aliquots`,
    icon: faPercent,
    title: "Alícuotas",
    description: "Qué parte de los gastos comunes paga cada propiedad.",
    action: isManager ? { to: `${base}/aliquots/create`, label: "Registrar alícuota" } : undefined,
  },
  isManager && {
    key: "income",
    to: `${base}/common-income`,
    icon: faCoins,
    color: "success",
    title: "Ingresos comunes",
    description: "Alquileres, multas y otros ingresos que no son cuotas.",
    action: { to: `${base}/common-income/create`, label: "Registrar ingreso" },
  },
  {
    key: "debt",
    to: `${base}/reports/properties`,
    icon: faChartBar,
    color: "secondary",
    title: "Deuda acumulada",
    description: "Reporte de la deuda de cada propiedad a lo largo del tiempo.",
  },
];

// Pagos que esperan que el manager los apruebe; cada uno abre su detalle,
// que es donde se aprueba o se rechaza.
const PendingPays = ({ pays, total, base }) => (
  <Card>
    <CardHeader className="d-flex justify-content-between align-items-center">
      <h5 className="mb-0">Pagos por aprobar</h5>
      <Link to={`${base}/payments/properties`} className="fs--1 font-weight-semi-bold">
        Ver todos los pagos
      </Link>
    </CardHeader>
    <Table responsive className="fs--1 mb-0">
      <thead className="bg-200 text-900">
        <tr>
          <th>Reportado</th>
          <th>Referencia</th>
          <th className="text-right">Monto</th>
          <th className="text-right">Acción</th>
        </tr>
      </thead>
      <tbody>
        {pays.map((pay) => (
          <tr key={pay.id}>
            <td className="text-nowrap">{formatDate(pay.created_at)}</td>
            <td>
              {pay.reference && pay.reference !== "undefined"
                ? pay.reference.toUpperCase()
                : "Sin referencia"}
            </td>
            <td className="text-right text-nowrap font-weight-semi-bold">
              <Money value={pay.amount} />
            </td>
            <td className="text-right">
              <Button tag={Link} to={`${base}/pay/${pay.id}`} color="primary" size="sm">
                Revisar
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
    {total > pays.length && (
      <p className="fs--1 text-600 mb-0 px-3 py-2 border-top border-200">
        Mostrando {pays.length} de {total} pagos por aprobar.
      </p>
    )}
  </Card>
);

PendingPays.propTypes = {
  pays: PropTypes.array.isRequired,
  total: PropTypes.number.isRequired,
  base: PropTypes.string.isRequired,
};

const FinanceCollections = ({ data, base, role }) => {
  const isManager = role === "manager";
  const cards = cardsFor({ base, isManager, data }).filter(Boolean);

  return (
    <>
      <p className="text-700 mb-3">
        Todo lo que entra al condominio: las cuotas de los propietarios, sus pagos y los
        ingresos comunes.
      </p>
      <Row>
        {cards.map(({ key, ...card }) => (
          <Col key={key} md={6} xl={4} className="mb-3">
            <HubLinkCard {...card} />
          </Col>
        ))}
      </Row>
      {isManager && data.pendingPays.items.length > 0 && (
        <PendingPays pays={data.pendingPays.items} total={data.pendingPays.total} base={base} />
      )}
    </>
  );
};

FinanceCollections.propTypes = {
  data: PropTypes.object.isRequired,
  base: PropTypes.string.isRequired,
  role: PropTypes.string,
};

export default FinanceCollections;
